import styles from 'components/PageLayout/Header/styles.module.scss';

import React, { FC, HTMLProps, useCallback, useRef } from 'react';
import cx from 'clsx';

import { CloverLogo } from 'components/CloverLogo';
import { HEADER_TEST_ID } from 'components/PageLayout/Header/consts';
import { SmartLink } from 'components/SmartLink';

import { useBodyWithoutScroll } from 'hooks/useBodyWithoutScroll/useBodyWithoutScroll';

import { KEY } from 'util/Input';
import { useWindowSize } from 'hooks/breakpoints';
import { COLO_COOKIE_BANNER_SEEN } from 'components/CookieBanner.const';
import { LocalStorage } from 'util/Storage';
import { CSSTransition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import { useTranslation } from 'react-i18next';
import { LoginButton } from 'components/LoginButton';
import { useCartPreview } from 'hooks/context/CartPreview';
import { CartPage } from 'components/CartPage';
import { useMerchantState } from 'hooks/context/MerchantState';

type HeaderProps = HTMLProps<HTMLElement> & {
  hideLogo?: boolean;
  logoClassName?: string;
  to?: string;
};

export const Header: FC<HeaderProps> = ({ children, logoClassName, hideLogo, className, to, ...props }) => {
  const { t } = useTranslation();
  const { cartPreviewVisible, closeCartPreview } = useCartPreview();
  const overlayRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useWindowSize();
  const cookieBannerExists = !LocalStorage.tryGetItem(COLO_COOKIE_BANNER_SEEN);
  const { showCustomerAuthState } = useMerchantState();

  useBodyWithoutScroll(cartPreviewVisible);

  const handleOverlayClick = useCallback(e => {
    // If clicking the overlay and not the children elements
    if (e.target === overlayRef.current) {
      // Prevent clicking anything under the overlay
      e.preventDefault();
      closeCartPreview();
    }
  }, [closeCartPreview]);

  const handleKeyDown = useCallback(key => {
    if (key === KEY.ESC) closeCartPreview();
  }, [closeCartPreview]);

  return (
    <header className={cx(styles.header, className)} {...props} data-testid={HEADER_TEST_ID}>
      <div className={styles.container}>
        <>{!hideLogo && (<>
          {to && (
            <SmartLink
              title="Clover"
              to={to}
              className={cx(styles.logoContainer, logoClassName)}
              aria-label={t('menu')}
            >
              <CloverLogo focusable={false} aria-hidden />
            </SmartLink>
          )}
          {!to && (
            <div className={cx(styles.logoContainer, logoClassName)}>
              <CloverLogo />
            </div>
          )}
          </>)}
          {showCustomerAuthState && !hideLogo && !isDesktop && <LoginButton className={cx(styles.mobile)} />}
        </>
        {children}
      </div>
      <CSSTransition
        in={cartPreviewVisible}
        timeout={{ exit: 500 }}
        onExit={closeCartPreview}
        unmountOnExit
        nodeRef={overlayRef}
      >
        {(state: TransitionStatus) => {
          const isOpening = ['entering', 'entered'].includes(state);
          const isClosing = ['exiting', 'exited'].includes(state);

          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <div
              role={'dialog'}
              tabIndex={-1}
              onKeyDown={handleKeyDown}
              className={cx(styles.cartPreviewOverlay, {
                [styles.overlayHeightWithCookieBanner]: cookieBannerExists,
                [styles.overlayEntering]: isOpening,
                [styles.overlayExiting]: isClosing
              })}
              onClick={handleOverlayClick}
              ref={overlayRef}
              aria-label={t('cart.title')}>
              <div className={cx(styles.cartPreview, {
                [styles.entering]: isOpening,
                [styles.exiting]: isClosing
              })}>
                <CartPage closeCartPreview={closeCartPreview} />
              </div>
            </div>
          );
        }}
      </CSSTransition>
    </header>
  );
};
